import { Controller } from "@hotwired/stimulus";
import { verifyInterface } from "../utils";
import * as Turbo from "@hotwired/turbo";
import * as lo from "lodash";
import {
  addTerminalAuthorizationOnTurboFetch,
  AssignScore,
  goToInit,
  TerminalInitialState,
  terminalRequest,
  verifySocketWatchdog,
} from "./terminal/utils";
import { Config } from "./terminal/modes/config";
import { AssignEmployee, AssignStore } from "./terminal/modes/assign";
import { Scan } from "./terminal/modes/scan";

export default class extends Controller {
  /**
   * # Terminal Controller.
   */
  static targets = [
    "addTile",
    "addTileTemplate",
    "assignedAssetsInput",
    "backdrop",
    "calibProcess",
    "employeeIdInput",
    "emptyTileTemplate",
    "goingAwayModal",
    "skeletonTileTemplate",
    "tiles",
  ];
  static values = {
    // static files
    cancelMp3: String,
    errorMp3: String,
    newAssetScannedMp3: String,
    toEmployeeMp3: String,
    toStoreMp3: String,
    // urls
    assetTileUrl: String,
    assignSuccessUrl: String,
    assignStoreUrl: String,
    flagAssetsDirtyUrl: String,
    pickEmployeeUrl: String,
    scanUrl: String,
    toggleLockedUrl: String,
    // current page
    urlName: String,
  };

  initialize() {
    if (!window.terminal)
      window.terminal = {
        state: lo.cloneDeep(TerminalInitialState),
      };
    this.wsMessageQueue = [];
  }

  requiredInterface = ["backdropTarget"];

  async connect() {
    verifyInterface(this, {
      requiredInterface: this.requiredInterface,
      allValues: true,
    });
    this.handleInteractionState(window.terminal.state.active);

    switch (this.urlNameValue) {
      case "terminal-scan":
        this.mode = new Scan(this);
        break;
      case "terminal-config":
        this.mode = new Config(this);
        break;
      case "terminal-assign-store":
        this.mode = new AssignStore(this);
        break;
      case "terminal-pick-employee":
        this.mode = new AssignEmployee(this);
        break;
      default:
      // console.debug("no mode for ", this.urlNameValue);
    }
    await this.ensureSocket();
    this.ensureTerminalData();
  }

  disconnect() {
    super.disconnect();
    if (this.mode && this.mode.deConstructor) {
      this.mode.deConstructor();
    }
  }

  ensureTerminalData() {
    if (window.terminal.data) {
      this.handleTerminalData(window.terminal.data);
    } else {
      this.send({ action: "requestTerminalData" });
    }
  }

  async ensureSocket() {
    if (!window.terminalSocketWatchdog) {
      window.terminalSocketWatchdog = setInterval(verifySocketWatchdog, 2000);
    }
    if (
      !(
        window.terminal.socket &&
        window.terminal.socket.readyState === WebSocket.OPEN
      )
    ) {
      await this.initializeSocket();
    }
    this.socket = window.terminal.socket;
    while (this.wsMessageQueue.length > 0) {
      this.send(this.wsMessageQueue.shift());
    }
    this.socket.onclose = goToInit;
    this.socket.onmessage = ({ data }) => {
      const event = JSON.parse(data);
      console.debug(event);
      switch (event.action) {
        case "terminalData":
          this.handleTerminalData(event.terminalData);
          break;
        case "interactionState":
          this.handleInteractionState(event.active);
          break;
        default:
          if (window.terminal.state.active && this.mode) {
            this.mode.handleEvent(event);
          }
      }
    };
  }

  send(data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return this.socket.send(JSON.stringify(data));
    }
    this.wsMessageQueue.push(data);
  }

  handleTerminalData(terminalData) {
    this.terminalData = terminalData;
    const isNew = window.terminal.data !== terminalData;
    addTerminalAuthorizationOnTurboFetch();
    if (isNew) {
      window.terminal.data = terminalData;
      return Turbo.visit(this.scanUrlValue);
    }
  }

  async initializeSocket() {
    window.terminal.socket = new WebSocket("ws://localhost:6789");
    while (window.terminal.socket.readyState !== WebSocket.OPEN) {
      await new Promise((r) => setTimeout(r, 100));
    }
  }

  unassignManually(event) {
    this.mode.unassignManually(event);
  }

  assignManually(event) {
    this.mode.assignManually(event);
  }

  requestRestart() {
    this.send({ action: "requestRestart" });
    Turbo.renderStreamMessage(`
      <turbo-stream action="append" target="toast_stream">
        <template>
          <div class="toast bg-success text-white fw-bold">
            <div class="toast-body">Neustart angefordert.</div>
          </div>
        </template>
      </turbo-stream>
    `);
  }

  handleInteractionState(active) {
    const modeHasAwayHandler = this.mode && "awayHandler" in this.mode;
    const awayHandler = modeHasAwayHandler ? this.mode.awayHandler : this;
    if (active) {
      return awayHandler.handleGoingActive();
    }
    return awayHandler.handleGoingInactive();
  }

  handleGoingActive() {
    this.backdropTarget.style.display = "none";
    window.terminal.state.active = true;
  }

  async handleGoingInactive() {
    this.backdropTarget.style.display = "block";
    this.flagAssetsDirty({ includeAssigned: true }).catch(console.error);
    if (window.terminal.state.active !== false) {
      // only do this once, as we trigger this on every `connect`
      window.terminal.state.active = false;
      return Turbo.visit(this.scanUrlValue);
    }
  }

  async getWarnings({ params: { url } }) {
    const turboResponse = await terminalRequest(url, { method: "GET" });
    if (!turboResponse.is_stream) {
      if (turboResponse.html !== "{}") {
        console.error("Expected stream or empty json, got", turboResponse);
      }
      return;
    }
    Turbo.renderStreamMessage(turboResponse.html);
  }

  async flagAssetsDirty(options = { includeAssigned: false }) {
    if (window.terminal.handleGoingInactiveLock) return;
    window.terminal.handlingGoingInactiveLock = true;

    try {
      const modeHandlesDetects = !!(this.mode && "handledDetects" in this.mode);
      const detects = modeHandlesDetects
        ? this.mode.handledDetects
        : window.terminal.handledDetects;
      if (modeHandlesDetects) {
        this.mode.handledDetects = {};
      }
      window.terminal.handledDetects = {};

      if (lo.isEmpty(detects)) return;

      const toFlagFilter = ([_, score]) => {
        if (score === AssignScore.ManuallyUnassigned) return true;
        if (!options.includeAssigned) return false;
        return AssignScore.isAssigned(score);
      };

      const idListToFlag = Object.entries(detects)
        .filter(toFlagFilter)
        .map(([assetId, _]) => Number(assetId));

      if (!lo.isEmpty(idListToFlag)) {
        const formData = new FormData();
        formData.append("asset_id_list", idListToFlag);
        const turboResponse = await terminalRequest(
          this.flagAssetsDirtyUrlValue,
          {
            formData,
          },
        );
        if (!turboResponse.response.ok) {
          console.error(
            "Failed to flag Assets dirty. asset_id_list: ",
            idListToFlag,
            "\n response:",
            turboResponse.response,
          );
        }
      }
    } finally {
      delete window.terminal.handlingGoingInactiveLock;
    }
  }
}
